import { Component, ComponentFactoryResolver, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { Guid } from 'guid-typescript';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, interval, of } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, take, takeUntil, timeout } from 'rxjs/operators';
import { Modules, RoutingPath } from './core/Enum';
import { GlobalComponent, MenuModel, PermissionModel } from './core/Models';
import { CommonService, ModulePermission } from './core/Services';
import { CompanyState, MenuState, SetModulePermission } from './core/Store';
import { ViewContainerRef } from '@angular/core';
import { AuthResolverService } from './core/Guards/auth.resolver';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  @Select(MenuState.moduleName)
  moduleName$: Observable<string>;

  moduleId = Modules;

  @Select(MenuState.moduleId)
  moduleId$: Observable<number>;

  routingPath = RoutingPath;
  activeRouteLink = '';
  userId?: Guid;

  @Select(MenuState.menu)
  menuList$: Observable<Array<MenuModel>>;

  @Select(CompanyState.companyCount)
  companyCount$: Observable<number>;

  moduleEnum = Modules;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private store: Store,
    private globalComponent: GlobalComponent,
    private spinner: NgxSpinnerService,
    private modulePermission: ModulePermission,
    private resolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef,
    public commonService: CommonService,
    public authResolverService: AuthResolverService,
    public oidcSecurityService: OidcSecurityService,
    private cookieService: CookieService
  ) {
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.activeRouteLink = this.normalizeRouteLink(event.url);

        if (
          (event.url === '/' || event.url === '/dashboard') &&
          !this.hasBookkeepingPermission()
        ) {
          this.router.navigate([RoutingPath.OpeningBalance], {
            replaceUrl: true,
          });
        }
      }
    });

    this.userId = this.globalComponent.getLoggedInUserId();
  }

  private hasBookkeepingPermission(): boolean {
    const showBookkeepingValue = localStorage.getItem('isShowBookkeeping');
    if (showBookkeepingValue !== null) {
      return showBookkeepingValue === 'true';
    }

    const rawValue = localStorage.getItem('userPermission');
    if (!rawValue) {
      return false;
    }

    try {
      const permissions = JSON.parse(rawValue);
      if (!Array.isArray(permissions)) {
        return false;
      }

      const checkMenuTree = (items: any[]): boolean =>
        items.some((item) => {
          const name = item?.name?.toLowerCase?.();
          if (name === 'bookkeeping') {
            return true;
          }

          const subMenu = Array.isArray(item?.subMenu) ? item.subMenu : [];
          return subMenu.length > 0 && checkMenuTree(subMenu);
        });

      return checkMenuTree(permissions);
    } catch {
      return false;
    }
  }

  private getDefaultLandingRoute(): string {
    return this.hasBookkeepingPermission()
      ? RoutingPath.Dashboard
      : RoutingPath.OpeningBalance;
  }

  ngOnInit(): void {
    if (this.router.url === '/' || this.router.url === '') {
      this.router.navigateByUrl(this.getDefaultLandingRoute(), {
        replaceUrl: true,
      });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        const hasSession = this.cookieService.get('newToken') || localStorage.getItem('0-' + environment.clientId);
        if (!hasSession) {
          return;
        }
        this.spinner.show();
        this.oidcSecurityService.forceRefreshSession().pipe(
          timeout(10000),
          catchError(() => {
            return of({ isAuthenticated: false } as any);
          })
        ).subscribe((result) => {
          this.spinner.hide();
          if (!result.isAuthenticated) {
            localStorage.clear();
            sessionStorage.clear();
            this.cookieService.deleteAll('/');
            this.oidcSecurityService.authorize();
          }
        });
      }
    });

    const refreshInterval = 2 * 60 * 1000;

    interval(refreshInterval)
      .pipe(
        switchMap(() => {
          const cookieToken = this.cookieService.get('newToken');
          return cookieToken
            ? of(cookieToken)
            : this.oidcSecurityService.getAccessToken().pipe(take(1));
        })
      )
      .subscribe((tempToken) => {
        if (this.isTokenExpired(tempToken)) {
          this.authResolverService.resolve();
        }
      });

    this.moduleId$.subscribe((moduleId) => {
      if (moduleId === Modules.Import) {
        this.commonService.toggleMenu = false;
      }
    });
  }

  decodeToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decodedPayload = atob(payload);
      return JSON.parse(decodedPayload);
    } catch (error) {
      return null; // Token decoding failed
    }
  }

  isTokenExpired(token: string): boolean {
    const decodedToken = this.decodeToken(token);
    if (!decodedToken || !decodedToken.exp) {
      return true; // Token is invalid or doesn't have an expiration
    }

    const currentTime = Math.floor(Date.now() / 1000); // Convert to seconds
    return decodedToken.exp < currentTime;
  }

  changeOfRoutes(componentRef): void {
    this.commonService.toggleMenu = true;
    const navUrl = this.router.url.slice(0).split(';')[0];
    const splitUrl = navUrl.split('/');
    if (splitUrl[splitUrl.length - 1] === 'add') {
      this.commonService.toggleMenu = false;
    }

    this.spinner.show();

    this.menuList$
      .pipe(
        debounceTime(700),
        filter((data) => Array.isArray(data) && data.length > 0),
        take(1)
      )
      .subscribe((data) => {
        this.setData(
          this.normalizeRouteLink(this.router.url, true),
          data,
          this.resolver.resolveComponentFactory(componentRef.constructor)
            .selector
        );
      });
  }

  private normalizeRouteLink(url: string, withLeadingSlash = false): string {
    const routeWithoutMatrixParams = url.split(';')[0];
    const routeWithoutQueryParams = routeWithoutMatrixParams.split('?')[0];
    const normalizedRoute = routeWithoutQueryParams.startsWith('/')
      ? routeWithoutQueryParams.slice(1)
      : routeWithoutQueryParams;

    if (normalizedRoute === RoutingPath.CharityPreference) {
      return withLeadingSlash ? `/${normalizedRoute}` : normalizedRoute;
    }

    if (withLeadingSlash) {
      return routeWithoutMatrixParams.startsWith('/')
        ? routeWithoutMatrixParams
        : `/${routeWithoutMatrixParams}`;
    }

    return routeWithoutMatrixParams.startsWith('/')
      ? routeWithoutMatrixParams.slice(1)
      : routeWithoutMatrixParams;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  emitPermissionData(data: any, componentName: any): void {
    const permissionData = new PermissionModel();
    permissionData.data = data;
    permissionData.componentName = componentName;

    this.modulePermission.permissionData.next(permissionData);
  }

  setData(event: any, data: any, componentName: any): void {
    const normalizedEvent = this.normalizeRouteLink(event, true);

    data.forEach((x) => {
      if (
        x.url === normalizedEvent.slice(1) ||
        x.addUrl === normalizedEvent.slice(1)
      ) {
        this.store.dispatch(new SetModulePermission(x)).subscribe();
        this.emitPermissionData(x, componentName);
      } else {
        x.subMenu.map((y) => {
          if (
            y.url === normalizedEvent.slice(1) ||
            y.addUrl === normalizedEvent.slice(1).split(';')[0]
          ) {
            this.store.dispatch(new SetModulePermission(y)).subscribe();
            this.emitPermissionData(y, componentName);
          } else {
            y.subMenu.map((z) => {
              if (
                z.url === normalizedEvent.slice(1) ||
                z.addUrl === normalizedEvent.slice(1).split(';')[0]
              ) {
                this.store.dispatch(new SetModulePermission(z)).subscribe();
                this.emitPermissionData(z, componentName);
              } else {
                z.subMenu.map((z1) => {
                  if (
                    z1.url === normalizedEvent.slice(1) ||
                    z1.addUrl === normalizedEvent.slice(1).split(';')[0]
                  ) {
                    this.store
                      .dispatch(new SetModulePermission(z1))
                      .subscribe();
                    this.emitPermissionData(z1, componentName);
                  } else{
                      z1.subMenu.map((z2) => {
                      if (
                        z2.url === normalizedEvent.slice(1) ||
                        z2.addUrl === normalizedEvent.slice(1).split(';')[0]
                      ) {
                        this.store
                          .dispatch(new SetModulePermission(z2))
                          .subscribe();
                        this.emitPermissionData(z2, componentName);

                      }
                    });

                  }
                });
              }
            });
          }
        });
      }
    });
  }

  onIconClick(): void {
    const saved = localStorage.getItem('isSidebarPinned');

    if (saved === null) {
      this.commonService.toggleMenu = !this.commonService.toggleMenu;
      return;
    }

    const isSidebarPinned = JSON.parse(saved);
    if (!isSidebarPinned) {
      this.commonService.toggleMenu = !this.commonService.toggleMenu;
    }
  }
}